import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert base64 to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

// Check if push is already subscribed
export async function isPushSubscribed() {
  if (!isPushSupported()) return false;
  
  try {
    // Check if service worker is registered
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length === 0) return false;
    
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    console.error('Error checking push subscription:', error);
    return false;
  }
}

// Get VAPID public key from backend
async function getVapidPublicKey() {
  const response = await axios.get(`${API}/notifications/vapid-public-key`);
  return response.data.public_key;
}

// Register service worker
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker not supported');
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('Service Worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    throw error;
  }
}

// Subscribe to push notifications
export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }
  
  try {
    // Register service worker first
    await registerServiceWorker();
    
    // Get VAPID public key
    const vapidPublicKey = await getVapidPublicKey();
    
    // Wait for service worker to be ready
    const registration = await navigator.serviceWorker.ready;
    
    // Request permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }
    
    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
    
    // Send subscription to backend
    const subscriptionData = subscription.toJSON();
    await axios.post(`${API}/notifications/subscribe`, {
      endpoint: subscriptionData.endpoint,
      keys: subscriptionData.keys
    });
    
    console.log('Push subscription successful');
    return true;
  } catch (error) {
    console.error('Push subscription failed:', error);
    throw error;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush() {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      const subscriptionData = subscription.toJSON();
      
      // Notify backend
      await axios.delete(`${API}/notifications/unsubscribe`, {
        data: {
          endpoint: subscriptionData.endpoint,
          keys: subscriptionData.keys
        }
      });
      
      // Unsubscribe locally
      await subscription.unsubscribe();
    }
    
    console.log('Push unsubscription successful');
    return true;
  } catch (error) {
    console.error('Push unsubscription failed:', error);
    throw error;
  }
}

// Get notification preferences
export async function getNotificationPreferences() {
  const response = await axios.get(`${API}/notifications/preferences`);
  return response.data;
}

// Update notification preferences
export async function updateNotificationPreferences(preferences) {
  const response = await axios.put(`${API}/notifications/preferences`, preferences);
  return response.data;
}

// Send test notification
export async function sendTestNotification() {
  const response = await axios.post(`${API}/notifications/send-test`);
  return response.data;
}
